import { useCallback, useState } from 'react';
import { useAxiosSwagger } from './useAxiosSwagger';
import { describeError } from '../api/requeue';
import {
    ReprocessItemResult,
    ReprocessResult,
    reprocessInbound,
} from '../api/replay';

export interface ReprocessMonitor {
    /** What became of the last attempt, or null when none has run. */
    result: ReprocessItemResult | null;
    busy: boolean;
    error: string | null;
    /**
     * Offers one stored webhook body to the receive path again.
     *
     * Takes the two halves of the key rather than an object, because that is what the trace
     * has: an object whose webhook was never processed has no stored copy and no object guid,
     * so there is nothing else about it to pass along.
     */
    run: (eventType: string, eventId: string) => Promise<ReprocessResult | null>;
    dismiss: () => void;
}

/**
 * Offering a raw webhook that got stuck before it was ever understood.
 *
 * Separate from `useRequeue` because it is a different act on a different queue. A requeue
 * writes a message naming a stored copy of the connector's own model; this writes the message
 * naming the raw body the other system sent, which is the only thing that exists when the
 * receive function refused the event and swallowed the refusal.
 *
 * One at a time on purpose. This is a repair by hand for an object somebody is looking at, and
 * the panel it belongs to shows exactly one object.
 */
export const useReprocess = (
    customer?: string,
    environment?: string
): ReprocessMonitor => {
    const axios = useAxiosSwagger();

    const [result, setResult] = useState<ReprocessItemResult | null>(null);
    const [busy, setBusy] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const run = useCallback(
        async (
            eventType: string,
            eventId: string
        ): Promise<ReprocessResult | null> => {
            if (!axios || !customer || !environment) {
                return null;
            }

            setBusy(true);
            setError(null);

            try {
                const response = await reprocessInbound(
                    axios,
                    customer,
                    environment,
                    [{ eventType, eventId }],
                    false
                );

                // One item in, one item out. The list shape is the endpoint's, and carrying it
                // into the panel would make every caller unpack it.
                setResult(response.results[0] ?? null);
                setError(response.error ?? null);

                return response;
            } catch (e) {
                setError(describeError(e));

                return null;
            } finally {
                setBusy(false);
            }
        },
        [axios, customer, environment]
    );

    const dismiss = useCallback(() => {
        setResult(null);
        setError(null);
    }, []);

    return { result, busy, error, run, dismiss };
};
